/**
 * Portal Auth event list — the slide-out panel behind each clickable stat on a
 * clinic's Portal Auth tab.
 *
 * TEMPORARY MIGRATION TOOLING - TO BE REMOVED AFTER CUTOVER.
 *
 * One row per event: when, which sign-in path, the `meta.reason` core
 * recorded, the details that go with it, and a link to the patient in odin.
 * No address, masked or not, and no IP or user agent — odin is where a person
 * is looked at; this panel only points there.
 *
 * The reason strip across the top is the same type and range unfiltered, so it
 * keeps offering every reason after one is picked.
 */

import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import { Button, Badge } from '../ui'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet'
import { portalAuthAPI } from '../../services/api'
import {
  DEFAULT_RANGE,
  RANGE_PRESETS,
  buildEventsParams,
  detailPairs,
  odinPatientUrl,
  reasonLabel,
  showsSurfaceFilter,
  surfaceLabel,
} from '../../lib/portalAuthEvents'

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

function formatTime(iso) {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

/** A clickable breakdown chip; the active one is filled. */
function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}>
      <Badge variant={active ? 'default' : 'outline'} className="cursor-pointer">
        {children}
      </Badge>
    </button>
  )
}

/**
 * @param {{ clinicId: string, stat: { type: string, purpose?: string, reason?: string, title: string } | null, onClose: () => void }} props
 *   `stat` null means closed. `reason` pre-selects that reason's filter, for a
 *   stat that is one reason of a type (a row of "Failed by reason").
 */
export default function PortalAuthEventsPanel({ clinicId, stat, onClose }) {
  const [filters, setFilters] = useState({ range: DEFAULT_RANGE, reason: stat?.reason || '', surface: '' })
  const [rows, setRows] = useState([])
  const [breakdown, setBreakdown] = useState({ reasons: [], surfaces: [] })
  const [nextCursor, setNextCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  // A different stat starts from a clean slate, not the last one's filters.
  useEffect(() => {
    setFilters({ range: DEFAULT_RANGE, reason: stat?.reason || '', surface: '' })
  }, [stat?.type, stat?.purpose, stat?.reason])

  const load = useCallback(async (cursor) => {
    if (!stat) return
    if (cursor) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const response = await portalAuthAPI.getEvents(clinicId, buildEventsParams(stat, filters, { cursor }))
      const data = response.data.data
      setRows((current) => (cursor ? [...current, ...data.rows] : data.rows))
      setBreakdown({ reasons: data.reasons || [], surfaces: data.surfaces || [] })
      setNextCursor(data.nextCursor)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load events'))
      if (!cursor) setRows([])
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [clinicId, stat, filters])

  useEffect(() => {
    load(null)
  }, [load])

  const toggle = (key, value) =>
    setFilters((current) => ({ ...current, [key]: current[key] === value ? '' : value }))

  const reasonTotal = breakdown.reasons.reduce((sum, entry) => sum + entry.count, 0)

  return (
    <Sheet open={Boolean(stat)} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{stat?.title}</SheetTitle>
          <SheetDescription>
            <span className="font-mono">{stat?.type}</span>
            {stat?.purpose && <> · purpose <span className="font-mono">{stat.purpose}</span></>}
            {' '}— one row per event, newest first. Open a patient in odin to see who.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2">
          {RANGE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              size="sm"
              variant={filters.range === preset.key ? 'default' : 'outline'}
              onClick={() => setFilters((current) => ({ ...current, range: preset.key }))}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Reasons · {reasonTotal.toLocaleString()} in range — click to filter
          </p>
          <div className="flex flex-wrap gap-2">
            {breakdown.reasons.length === 0 && !loading && (
              <span className="text-sm text-muted-foreground">None in this range.</span>
            )}
            {breakdown.reasons.map((entry) => (
              <Chip
                key={String(entry.value)}
                active={filters.reason === entry.value}
                onClick={() => toggle('reason', entry.value)}
              >
                {reasonLabel(entry.value)} · {entry.count.toLocaleString()}
              </Chip>
            ))}
          </div>
          {showsSurfaceFilter(breakdown.surfaces) && (
            <div className="flex flex-wrap gap-2">
              {breakdown.surfaces.map((entry) => (
                <Chip
                  key={String(entry.value)}
                  active={filters.surface === entry.value}
                  onClick={() => toggle('surface', entry.value)}
                >
                  {surfaceLabel(entry.value)} · {entry.count.toLocaleString()}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex-1 overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Path</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium">Details</th>
                <th className="px-3 py-2 font-medium">Patient</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading events…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No events in this range.
                  </td>
                </tr>
              )}
              {!loading && rows.map((row) => {
                const odinUrl = odinPatientUrl(row.customerId)
                return (
                  <tr key={row.id} className="border-t align-top">
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatTime(row.at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{surfaceLabel(row.surface, row.type)}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {reasonLabel(row.reason)}
                      {row.legacy && (
                        <Badge
                          variant="outline"
                          className="ml-2 font-sans"
                          title="Written before the migration chain existed; not counted as a login failure"
                        >
                          legacy
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {detailPairs(row.details).map((pair) => <div key={pair}>{pair}</div>)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {odinUrl ? (
                        <a
                          href={odinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Open in odin <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {nextCursor && !loading && (
          <Button variant="outline" onClick={() => load(nextCursor)} disabled={loadingMore}>
            {loadingMore && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Load more
          </Button>
        )}
      </SheetContent>
    </Sheet>
  )
}
